// Per-store serialized auto-save queue + latest-config cache.
//
// The inline auto-save panels post the FULL config on every change, so two
// overlapping requests can land out of order and clobber each other. The queue
// also has to outlive any single panel:
//   - an inline panel unmounts the moment it's collapsed, while its POST may
//     still be in flight;
//   - Time Clock and Timecard Flagging are two views of the SAME TimecardConfig,
//     so a save started by one can still be running when the other mounts.
// If a freshly-mounted panel fetched the server snapshot mid-save it would build
// its next full payload from stale data and revert the earlier edit.
//
// Holding the queue + the latest snapshot here (keyed by store id, outside React)
// means: writes for a store are strictly ordered, a change made mid-save
// coalesces into ONE follow-up post built from the newest snapshot, and a panel
// that mounts while a save is in flight seeds from the cache instead of the
// stale server state.
// `onOrphanedError` is called when a save fails with NO panel subscribed — the
// panel was collapsed/switched mid-save, so there is nobody left to toast or
// resync. Without it those failures would be silent.
export function createConfigAutoSaveQueue({ onOrphanedError } = {}) {
  const entries = new Map();

  const ensure = (storeId) => {
    let entry = entries.get(storeId);
    if (!entry) {
      entry = {
        latest: undefined,
        inFlight: false,
        pending: false,
        // Bumped on every commit. A GET that started before a commit and
        // resolved after it carries a snapshot older than our cache, even if
        // the queue has since drained and looks idle.
        version: 0,
        listeners: new Set(),
      };
      entries.set(storeId, entry);
    }
    return entry;
  };

  const isBusyEntry = (entry) => Boolean(entry && (entry.inFlight || entry.pending));

  const notifyBusy = (entry) => {
    entry.listeners.forEach((l) => l.onBusyChange?.(isBusyEntry(entry)));
  };

  // Runs one post from the latest snapshot, then drains anything queued while it
  // was in flight — so the last request always carries current data.
  async function drain(storeId, post) {
    const entry = ensure(storeId);
    entry.inFlight = true;
    notifyBusy(entry);

    try {
      await post(storeId, entry.latest);
    } catch (error) {
      entry.inFlight = false;
      entry.pending = false;
      // Drop the cache so the next mount refetches the server's actual state.
      entry.latest = undefined;
      notifyBusy(entry);
      if (entry.listeners.size > 0) {
        entry.listeners.forEach((l) => l.onError?.(error));
      } else {
        // The panel unmounted mid-save, so no listener remains to report this.
        // Surface it globally; the cleared cache makes the next mount refetch.
        onOrphanedError?.(error, storeId);
      }
      return;
    }

    if (entry.pending) {
      entry.pending = false;
      await drain(storeId, post);
      return;
    }

    entry.inFlight = false;
    notifyBusy(entry);
  }

  return {
    getLatest: (storeId) => entries.get(storeId)?.latest,
    isBusy: (storeId) => isBusyEntry(entries.get(storeId)),
    getVersion: (storeId) => entries.get(storeId)?.version ?? 0,
    // True when a GET started at (startedBusy, startVersion) may have read the
    // server before a save committed — i.e. its payload can't be trusted over
    // the cache. Callers capture both BEFORE issuing the GET.
    isFetchStale: (storeId, startedBusy, startVersion) => {
      const entry = entries.get(storeId);
      return Boolean(
        startedBusy ||
          isBusyEntry(entry) ||
          (entry?.version ?? 0) !== startVersion
      );
    },
    setLatest: (storeId, data) => {
      ensure(storeId).latest = data;
    },
    // Panels subscribe while mounted; unsubscribing on unmount lets the queue
    // keep draining without touching a dead component's state.
    //
    // The current busy state is replayed on attach. A save outlives the panel
    // that started it, so a panel mounting onto a busy key (a sibling view of
    // the same config row, or a remount after collapse) would otherwise believe
    // the key is idle until the next commit.
    subscribe: (storeId, listener) => {
      const entry = ensure(storeId);
      entry.listeners.add(listener);
      listener.onBusyChange?.(isBusyEntry(entry));
      return () => entry.listeners.delete(listener);
    },
    // Merge the patch into the cached snapshot and schedule a serialized save.
    // Returns the merged snapshot so the caller can render it optimistically.
    // `base` seeds the snapshot when the cache is empty (e.g. a failed save
    // cleared it) — without it the merged patch would be a PARTIAL config and
    // the full-payload post would null every omitted field.
    commit: (storeId, patch, post, base) => {
      const entry = ensure(storeId);
      entry.latest = { ...(entry.latest ?? base ?? {}), ...patch };
      entry.version += 1;
      if (entry.inFlight) {
        entry.pending = true;
        notifyBusy(entry);
        return entry.latest;
      }
      drain(storeId, post);
      return entry.latest;
    },
  };
}
